import React from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import { MedicWorkloadDto, MedicWorkloadItemDto } from '../../models/analytics/medicWorkloadDto';

interface MedicWorkloadDataTableProps {
    data: MedicWorkloadDto;
}

export const MedicWorkloadDataTable: React.FC<MedicWorkloadDataTableProps> = ({ data }) => {
    // Calculate totals for each interval
    const intervalTotals: Record<string, number> = {};
    let grandTotal = 0;

    data.medics.forEach(medic => {
        grandTotal += medic.totalCount;
        Object.entries(medic.intervalCounts).forEach(([key, count]) => {
            intervalTotals[key] = (intervalTotals[key] || 0) + count;
        });
    });

    // Calculate percentage for a value relative to total
    const getPercentage = (value: number, total: number): number => {
        if (total === 0) return 0;
        return Math.round((value / total) * 100);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, flexShrink: 0 }}>
                Детализация по интервалам
            </Typography>
            <TableContainer 
                component={Paper} 
                sx={{ 
                    height: 'calc(100vh - 400px)',
                    maxHeight: 'calc(100vh - 400px)',
                    overflowX: 'auto',
                    overflowY: 'auto',
                    flex: 1,
                    '& .MuiTableCell-root': {
                        whiteSpace: 'nowrap',
                        padding: '8px 6px',
                    }
                }}
            >
                <Table stickyHeader sx={{ minWidth: 650, width: 'max-content' }}>
                    <TableHead>
                        <TableRow>
                            <TableCell 
                                sx={{ 
                                    position: 'sticky', 
                                    left: 0, 
                                    zIndex: 3,
                                    backgroundColor: 'white',
                                    minWidth: 150,
                                    maxWidth: 150,
                                    width: 150,
                                    fontWeight: 600,
                                    borderRight: '1px solid rgba(224, 224, 224, 1)',
                                    boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
                                }}
                            >
                                Мед. работник
                            </TableCell>
                            {data.intervals.map((interval) => (
                                <TableCell 
                                    key={interval.key}
                                    align="center"
                                    sx={{ fontWeight: 600, minWidth: 100, maxWidth: 120, width: 120 }}
                                >
                                    {interval.label}
                                </TableCell>
                            ))}
                            <TableCell 
                                align="center"
                                sx={{ fontWeight: 600, minWidth: 100, maxWidth: 120, width: 120 }}
                            >
                                Итого
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.medics.map((medic: MedicWorkloadItemDto) => (
                            <TableRow key={medic.medicId}>
                                <TableCell 
                                    sx={{ 
                                        position: 'sticky', 
                                        left: 0, 
                                        zIndex: 2,
                                        backgroundColor: 'white',
                                        fontWeight: 500,
                                        minWidth: 150,
                                        maxWidth: 150,
                                        width: 150,
                                        borderRight: '1px solid rgba(224, 224, 224, 1)',
                                        boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
                                    }}
                                >
                                    {medic.shortFio}
                                </TableCell>
                                {data.intervals.map((interval) => {
                                    const count = medic.intervalCounts[interval.key] || 0;
                                    const intervalTotal = intervalTotals[interval.key] || 0;
                                    const percentage = getPercentage(count, intervalTotal);
                                    
                                    return (
                                        <TableCell key={interval.key} align="center">
                                            {count > 0 ? `${count} (${percentage}%)` : '-'}
                                        </TableCell>
                                    );
                                })}
                                <TableCell align="center" sx={{ fontWeight: 500 }}>
                                    {medic.totalCount > 0 
                                        ? `${medic.totalCount} (${getPercentage(medic.totalCount, grandTotal)}%)` 
                                        : '-'}
                                </TableCell>
                            </TableRow>
                        ))}
                        {/* Total row */}
                        <TableRow sx={{ backgroundColor: '#f5f5f5', fontWeight: 600 }}>
                            <TableCell 
                                sx={{ 
                                    position: 'sticky', 
                                    left: 0, 
                                    zIndex: 2,
                                    backgroundColor: '#f5f5f5',
                                    fontWeight: 600,
                                    minWidth: 150,
                                    maxWidth: 150,
                                    width: 150,
                                    borderRight: '1px solid rgba(224, 224, 224, 1)',
                                    boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
                                }}
                            >
                                Итого:
                            </TableCell>
                            {data.intervals.map((interval) => {
                                const total = intervalTotals[interval.key] || 0;
                                return (
                                    <TableCell key={interval.key} align="center" sx={{ fontWeight: 600 }}>
                                        {total > 0 ? total : '-'}
                                    </TableCell>
                                );
                            })}
                            <TableCell align="center" sx={{ fontWeight: 600 }}>
                                {grandTotal}
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

